import React, { useState, useMemo } from 'react';
import { 
  Package, AlertTriangle, CheckCircle, Plus, Minus, Search, 
  Filter, RefreshCw, ShoppingBag, Send, Edit2, Check, X, 
  ShieldAlert, DollarSign, ArrowUpRight
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import toast from 'react-hot-toast';

export default function InventoryManager({ activeBusiness, onUpdateBusiness, isReadOnlyDemo }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all' | 'low' | 'ok'
  const [editingId, setEditingId] = useState(null);
  const [editStock, setEditStock] = useState('');
  const [editMinStock, setEditMinStock] = useState('');
  const [loadingId, setLoadingId] = useState(null);

  const services = useMemo(() => {
    return activeBusiness?.services || [];
  }, [activeBusiness]);

  // Asegurar que los ítems tengan propiedades por defecto de inventario
  const normalizedServices = useMemo(() => {
    return services.map(item => ({
      ...item,
      stock: typeof item.stock === 'number' ? item.stock : 15,
      stock_minimo: typeof item.stock_minimo === 'number' ? item.stock_minimo : 5,
      control_stock: item.control_stock !== false
    }));
  }, [services]);

  // Estadísticas del inventario
  const stats = useMemo(() => {
    let totalItems = normalizedServices.length;
    let criticalCount = 0;
    let totalValue = 0;

    normalizedServices.forEach(item => {
      if (item.stock <= item.stock_minimo) {
        criticalCount++;
      }
      totalValue += (Number(item.precio) || 0) * (item.stock || 0);
    });

    return {
      totalItems,
      criticalCount,
      okCount: totalItems - criticalCount,
      totalValue
    };
  }, [normalizedServices]);

  // Filtrar ítems
  const filteredItems = useMemo(() => {
    return normalizedServices.filter(item => {
      const matchesSearch = item.nombre?.toLowerCase().includes(searchTerm.toLowerCase());
      if (!matchesSearch) return false;
      if (filterType === 'low') return item.stock <= item.stock_minimo;
      if (filterType === 'ok') return item.stock > item.stock_minimo;
      return true;
    });
  }, [normalizedServices, searchTerm, filterType]);

  // Función para guardar stock modificado en Supabase
  const updateServicesInDb = async (updatedServices, successMessage = "Inventario actualizado") => {
    // 1. Optimistic / local state update
    if (onUpdateBusiness) {
      onUpdateBusiness({ ...activeBusiness, services: updatedServices });
    }

    if (isReadOnlyDemo) {
      toast.success(successMessage + " (Simulación Demo)");
      return;
    }

    try {
      if (!activeBusiness?.id) return;
      const { error } = await supabase
        .from('businesses')
        .update({ services: updatedServices })
        .eq('id', activeBusiness.id);

      if (error) {
        console.warn("Supabase stock update:", error.message);
      }
      toast.success(successMessage);
    } catch (err) {
      console.error("Error updating stock:", err);
      toast.success(successMessage + " (Guardado localmente)");
    }
  };

  // Ajustar stock rápido (+1 / -1 / +10)
  const handleAdjustStock = async (id, delta) => {
    setLoadingId(id);
    const updated = normalizedServices.map(item => {
      if (item.id === id) {
        const nextStock = Math.max(0, (item.stock || 0) + delta);
        return { ...item, stock: nextStock };
      }
      return item;
    });

    await updateServicesInDb(updated, delta > 0 ? `+${delta} agregados al stock` : `-${Math.abs(delta)} reducidos del stock`);
    setLoadingId(null);
  };

  // Guardar edición manual de valores de stock
  const handleSaveEdit = async (id) => {
    const newStock = parseInt(editStock, 10);
    const newMin = parseInt(editMinStock, 10);

    if (isNaN(newStock) || newStock < 0 || isNaN(newMin) || newMin < 0) {
      toast.error("Por favor ingresa cantidades numéricas válidas");
      return;
    }

    setLoadingId(id);
    const updated = normalizedServices.map(item => {
      if (item.id === id) {
        return {
          ...item,
          stock: newStock,
          stock_minimo: newMin
        };
      }
      return item;
    });

    await updateServicesInDb(updated, "Límites de stock guardados");
    setEditingId(null);
    setLoadingId(null);
  };

  // Generar pedido de reposición por WhatsApp
  const handleOrderSupplier = () => {
    const criticalItems = normalizedServices.filter(item => item.stock <= item.stock_minimo);
    if (criticalItems.length === 0) {
      toast.success("¡Todo tu stock está en niveles saludables! No se requiere reponer.");
      return;
    }

    let message = `Hola! Necesito pedido de reposición para ${activeBusiness?.nombre_negocio || 'el negocio'}:\n\n`;
    criticalItems.forEach(item => {
      const faltante = Math.max(10, (item.stock_minimo * 3) - item.stock);
      message += `• *${item.nombre}*: Solicito ${faltante} uds (Stock actual: ${item.stock} - Mínimo: ${item.stock_minimo})\n`;
    });
    message += `\nQuedo atento al envío y confirmación del pedido.`;

    const encoded = encodeURIComponent(message);
    window.open(`https://wa.me/?text=${encoded}`, '_blank');
  };

  return (
    <div className="space-y-6 mt-4">
      {/* Cabecera del Administrador de Inventario */}
      <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-extrabold text-seryi-heading flex items-center gap-2">
            <Package className="text-seryi-green" size={24} />
            Control Inteligente de Inventario y Alertas de Stock
          </h2>
          <p className="text-xs text-seryi-text mt-0.5">
            Supervisa existencias en tiempo real, define alertas de stock bajo y repón producto automáticamente.
          </p>
        </div>

        <button
          onClick={handleOrderSupplier}
          className="bg-seryi-green hover:bg-seryi-green/80 text-seryi-bg-dark font-bold px-5 py-3 rounded-2xl text-xs flex items-center justify-center gap-2 transition-all shadow-lg active:scale-95 shrink-0"
        >
          <Send size={15} />
          <span>Pedir Reposición a Proveedor</span>
        </button>
      </div>

      {/* Tarjetas de Métricas del Stock */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-seryi-bg border border-seryi-bg-light p-5 rounded-2xl space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase font-bold text-seryi-text tracking-wider">Valor del Inventario</span>
            <DollarSign size={16} className="text-emerald-400" />
          </div>
          <p className="text-2xl font-black text-emerald-400">
            ${stats.totalValue.toLocaleString('es-CO')}
          </p>
          <p className="text-[10px] text-seryi-text">Valor total en mercancía / servicios</p>
        </div>

        <div className="bg-seryi-bg border border-seryi-bg-light p-5 rounded-2xl space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase font-bold text-seryi-text tracking-wider">Alertas de Stock Bajo</span>
            <AlertTriangle size={16} className={stats.criticalCount > 0 ? "text-rose-400 animate-pulse" : "text-seryi-text"} />
          </div>
          <p className={`text-2xl font-black ${stats.criticalCount > 0 ? 'text-rose-400' : 'text-seryi-heading'}`}>
            {stats.criticalCount}
          </p>
          <p className="text-[10px] text-seryi-text">
            {stats.criticalCount > 0 ? "Ítems por debajo del umbral mínimo" : "Todos los ítems saludables"}
          </p>
        </div>

        <div className="bg-seryi-bg border border-seryi-bg-light p-5 rounded-2xl space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase font-bold text-seryi-text tracking-wider">Total Ítems Activos</span>
            <CheckCircle size={16} className="text-blue-400" />
          </div>
          <p className="text-2xl font-black text-blue-400">
            {stats.totalItems}
          </p>
          <p className="text-[10px] text-seryi-text font-semibold">Catálogo y productos controlados</p>
        </div>
      </div>

      {/* Barra de Búsqueda y Filtros */}
      <div className="bg-seryi-bg border border-seryi-bg-light p-5 rounded-3xl space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              onClick={() => setFilterType('all')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                filterType === 'all' 
                  ? 'bg-seryi-green text-seryi-bg-dark shadow-md' 
                  : 'bg-seryi-bg-dark text-seryi-text hover:text-seryi-heading'
              }`}
            >
              Todos ({stats.totalItems})
            </button>
            <button
              onClick={() => setFilterType('low')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                filterType === 'low' 
                  ? 'bg-rose-500 text-white shadow-md' 
                  : 'bg-seryi-bg-dark text-seryi-text hover:text-seryi-heading'
              }`}
            >
              🔴 Stock Crítico ({stats.criticalCount})
            </button>
            <button
              onClick={() => setFilterType('ok')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                filterType === 'ok' 
                  ? 'bg-emerald-500 text-seryi-bg-dark shadow-md' 
                  : 'bg-seryi-bg-dark text-seryi-text hover:text-seryi-heading'
              }`}
            >
              🟢 Saludables ({stats.okCount})
            </button>
          </div>

          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3.5 top-3 text-seryi-text" size={15} />
            <input
              type="text"
              placeholder="Buscar producto..."
              className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl pl-9 pr-4 py-2 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Tabla de Productos e Inventario */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-seryi-bg-light text-[10px] uppercase font-bold text-seryi-text tracking-wider">
                <th className="py-3 px-4">Producto / Servicio</th>
                <th className="py-3 px-4">Precio Unitario</th>
                <th className="py-3 px-4">Estado de Stock</th>
                <th className="py-3 px-4">Alerta Mínima</th>
                <th className="py-3 px-4 text-center">Ajuste Rápido (+ / -)</th>
                <th className="py-3 px-4 text-right">Acción</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-seryi-bg-light/50 text-xs">
              {filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-seryi-text italic">
                    No se encontraron productos en el inventario que coincidan con tu búsqueda.
                  </td>
                </tr>
              ) : (
                filteredItems.map(item => {
                  const isCritical = item.stock <= item.stock_minimo;
                  const isEditing = editingId === item.id;
                  const isLoading = loadingId === item.id;

                  return (
                    <tr key={item.id} className="hover:bg-seryi-bg-dark/40 transition-colors">
                      <td className="py-3.5 px-4 font-bold text-seryi-heading">
                        <div className="flex items-center gap-2.5">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${
                            isCritical ? 'bg-rose-500/20 text-rose-300 border border-rose-500/30' : 'bg-emerald-500/10 text-emerald-400'
                          }`}>
                            {isCritical ? <AlertTriangle size={14} /> : <Package size={14} />}
                          </div>
                          <div>
                            <p className="text-sm">{item.nombre}</p>
                            <p className="text-[10px] text-seryi-text">ID: {item.id?.slice?.(0, 8) || 'N/A'}</p>
                          </div>
                        </div>
                      </td>

                      <td className="py-3.5 px-4 font-mono text-seryi-heading">
                        ${Number(item.precio || 0).toLocaleString('es-CO')}
                      </td>

                      <td className="py-3.5 px-4">
                        {isEditing ? (
                          <div className="flex items-center gap-1.5">
                            <input
                              type="number"
                              className="w-20 bg-seryi-bg-dark border border-seryi-green rounded-lg px-2.5 py-1 text-xs text-seryi-heading font-bold focus:outline-none"
                              value={editStock}
                              onChange={(e) => setEditStock(e.target.value)}
                            />
                            <span className="text-[10px] text-seryi-text">uds</span>
                          </div>
                        ) : (
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black ${
                            isCritical 
                              ? 'bg-rose-500/20 text-rose-300 border border-rose-500/30 animate-pulse' 
                              : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                          }`}>
                            {isCritical ? <AlertTriangle size={12} /> : <CheckCircle size={12} />}
                            {item.stock} uds
                          </span>
                        )}
                      </td>

                      <td className="py-3.5 px-4">
                        {isEditing ? (
                          <div className="flex items-center gap-1.5">
                            <input
                              type="number"
                              className="w-20 bg-seryi-bg-dark border border-amber-500 rounded-lg px-2.5 py-1 text-xs text-seryi-heading font-bold focus:outline-none"
                              value={editMinStock}
                              onChange={(e) => setEditMinStock(e.target.value)}
                            />
                            <span className="text-[10px] text-seryi-text">mín</span>
                          </div>
                        ) : (
                          <span className="font-mono text-seryi-text text-xs">
                            Mín: {item.stock_minimo} uds
                          </span>
                        )}
                      </td>

                      <td className="py-3.5 px-4 text-center">
                        <div className="inline-flex items-center gap-1 bg-seryi-bg-dark p-1 rounded-xl border border-seryi-bg-light">
                          <button
                            onClick={() => handleAdjustStock(item.id, -1)}
                            disabled={isLoading}
                            className="w-7 h-7 bg-seryi-bg hover:bg-rose-500/20 hover:text-rose-400 text-seryi-heading rounded-lg flex items-center justify-center transition-all disabled:opacity-50 font-bold"
                            title="Restar 1"
                          >
                            <Minus size={13} />
                          </button>
                          <button
                            onClick={() => handleAdjustStock(item.id, 1)}
                            disabled={isLoading}
                            className="w-7 h-7 bg-seryi-bg hover:bg-emerald-500/20 hover:text-emerald-400 text-seryi-heading rounded-lg flex items-center justify-center transition-all disabled:opacity-50 font-bold"
                            title="Sumar 1"
                          >
                            <Plus size={13} />
                          </button>
                          <button
                            onClick={() => handleAdjustStock(item.id, 10)}
                            disabled={isLoading}
                            className="px-2 h-7 bg-seryi-bg hover:bg-indigo-500/20 hover:text-indigo-400 text-seryi-text rounded-lg flex items-center justify-center transition-all disabled:opacity-50 text-[10px] font-extrabold"
                            title="Ingresar +10 Uds (Caja / Reposición)"
                          >
                            +10
                          </button>
                        </div>
                      </td>

                      <td className="py-3.5 px-4 text-right">
                        {isEditing ? (
                          <div className="inline-flex items-center gap-1">
                            <button
                              onClick={() => handleSaveEdit(item.id)}
                              disabled={isLoading}
                              className="px-2.5 py-1 bg-seryi-green hover:bg-seryi-green/80 text-seryi-bg-dark rounded-lg text-[11px] font-extrabold transition-all"
                            >
                              Guardar
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="px-2.5 py-1 bg-seryi-bg-light hover:bg-slate-700 text-seryi-text rounded-lg text-[11px] transition-all"
                            >
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setEditingId(item.id);
                              setEditStock(item.stock.toString());
                              setEditMinStock(item.stock_minimo.toString());
                            }}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-seryi-bg-light hover:bg-seryi-green/10 hover:text-seryi-green text-seryi-text rounded-lg text-[11px] font-semibold transition-all"
                          >
                            <Edit2 size={12} />
                            Editar Límite
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
